import React, { useState } from 'react';
import type { PageDetailOut } from '../types/catalog';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface UnknownTemplatePanelProps {
  templateId: string | undefined;
  page: PageDetailOut;
}

/**
 * Shown when template_id is not supported yet.
 * Displays a clean panel with template_id and a collapsed JSON preview for debugging.
 */
export const UnknownTemplatePanel: React.FC<UnknownTemplatePanelProps> = ({
  templateId,
  page,
}) => {
  const [jsonOpen, setJsonOpen] = useState(false);
  const payload = page.page_json_v1 ?? null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="rounded-xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 overflow-hidden">
        <div className="px-5 py-4">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
            Template not supported yet
          </h2>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
            <span className="font-medium">template_id:</span>{' '}
            <code className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50">
              {templateId || 'unknown'}
            </code>
          </p>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            This page type is not implemented in the UI. Use the JSON below for debugging.
          </p>
        </div>

        <div className="border-t border-amber-200 dark:border-amber-800">
          <button
            type="button"
            onClick={() => setJsonOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-5 py-3 text-left text-sm font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors"
          >
            {jsonOpen ? (
              <ChevronDown className="w-4 h-4 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 shrink-0" />
            )}
            {jsonOpen ? 'Hide' : 'Show'} JSON preview
          </button>
          {jsonOpen && payload && (
            <pre className="p-4 text-xs overflow-auto max-h-64 bg-white dark:bg-gray-900 border-t border-amber-200 dark:border-amber-800 text-gray-800 dark:text-gray-200">
              {JSON.stringify(payload, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
